/* eslint-disable react/prop-types */
import React from 'react';

//Externals
import classNames from 'classnames';

//Material helpers
import { makeStyles } from '@mui/styles';
import styles from './styles';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { isAllowed } from 'helpers/auth';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  AssignmentIndOutlined,
  Description,
  ExitToApp,
  HouseOutlined,
  Info,
  LocalAtm,
  PeopleOutline,
  PersonOutline,
  ReportProblem,
} from '@mui/icons-material';
import { Fee } from 'icons';
import { logout } from 'js/actions/userActions';
import { clsx } from 'clsx';

const useStyles = makeStyles(styles);

export default function Sidebar(props) {
  const intl = useIntl();
  const { pathname } = useLocation();
  const { open, mobile, close } = props;
  const classes = useStyles();
  const rootClassName = classNames(classes.root);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);

  const finishSession = () => {
    dispatch(logout());
  };

  return (
    <Drawer
      anchor="left"
      className={rootClassName}
      open={open}
      variant={mobile ? 'temporary' : 'permanent'}
    >
      <nav
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open || mobile,
          [classes.drawerClose]: !open,
        })}
      >
        <Link
          to={user.home_url}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            alt="Metimur Logo"
            className={classes.logoImg}
            href={user.home_url}
            src={
              open
                ? '/images/logo_horizontal.png'
                : '/images/favicon_metimur.png'
            }
            width={open ? '180px' : '50px'}
          />
        </Link>
        <Divider className={classes.profileDivider} />
        <List component="div" disablePadding={true}>
          <ListItem
            className={clsx(classes.listItem, {
              [classes.activeListItem]: pathname === user.home_url,
              [classes.listItem]: pathname !== user.home_url,
            })}
            component={NavLink}
            onClick={close}
            to={`${user.home_url}`}
          >
            <Tooltip
              placement="right"
              title={intl.formatMessage({ id: 'app.barmenu.home' })}
            >
              <ListItemIcon className={classes.listItemIcon}>
                <HouseOutlined />
              </ListItemIcon>
            </Tooltip>
            <ListItemText
              className={clsx(classes.listItemText, {
                [classes.listItemText]: open,
                [classes.listItemTextHide]: !open,
              })}
              classes={{ primary: classes.listItemText }}
              primary={intl.formatMessage({ id: 'app.barmenu.home' })}
            />
          </ListItem>
          <ListItem
            className={clsx(classes.listItem, {
              [classes.activeListItem]: pathname === '/account',
              [classes.listItem]: pathname !== '/account',
            })}
            component={NavLink}
            onClick={close}
            to="/account"
          >
            <Tooltip
              placement="right"
              title={intl.formatMessage({ id: 'app.barmenu.myAccount' })}
            >
              <ListItemIcon className={classes.listItemIcon}>
                <PersonOutline />
              </ListItemIcon>
            </Tooltip>
            <ListItemText
              className={clsx(classes.listItemText, {
                [classes.listItemText]: open,
                [classes.listItemTextHide]: !open,
              })}
              classes={{ primary: classes.listItemText }}
              primary={intl.formatMessage({ id: 'app.barmenu.myAccount' })}
            />
          </ListItem>
          {isAllowed(user.permissions, ['user_read']) && (
            <ListItem
              className={clsx(classes.listItem, {
                [classes.activeListItem]: pathname === '/usersByCorps',
                [classes.listItem]: pathname !== '/usersByCorps',
              })}
              component={NavLink}
              onClick={close}
              to="/usersByCorps"
            >
              <Tooltip
                placement="right"
                title={intl.formatMessage({ id: 'app.barmenu.users' })}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <PeopleOutline />
                </ListItemIcon>
              </Tooltip>
              <ListItemText
                className={clsx(classes.listItemText, {
                  [classes.listItemText]: open,
                  [classes.listItemTextHide]: !open,
                })}
                classes={{ primary: classes.listItemText }}
                primary={intl.formatMessage({ id: 'app.barmenu.users' })}
              />
            </ListItem>
          )}

          {isAllowed(user.permissions, ['metimur_user_read']) && (
            <ListItem
              className={clsx(classes.listItem, {
                [classes.activeListItem]: pathname === '/usersMetimur',
                [classes.listItem]: pathname !== '/usersMetimur',
              })}
              component={NavLink}
              onClick={close}
              to="/usersMetimur"
            >
              <Tooltip
                placement="right"
                title={intl.formatMessage({ id: 'app.barmenu.metUsers' })}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <PeopleOutline />
                </ListItemIcon>
              </Tooltip>
              <ListItemText
                className={clsx(classes.listItemText, {
                  [classes.listItemText]: open,
                  [classes.listItemTextHide]: !open,
                })}
                classes={{ primary: classes.listItemText }}
                primary={intl.formatMessage({ id: 'app.barmenu.metUsers' })}
              />
            </ListItem>
          )}

          {isAllowed(user.permissions, ['role_read']) && (
            <ListItem
              className={clsx(classes.listItem, {
                [classes.activeListItem]: pathname === '/roles',
                [classes.listItem]: pathname !== '/roles',
              })}
              component={NavLink}
              onClick={close}
              to="/roles"
            >
              <Tooltip
                placement="right"
                title={intl.formatMessage({ id: 'app.barmenu.roles' })}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <AssignmentIndOutlined />
                </ListItemIcon>
              </Tooltip>
              <ListItemText
                className={clsx(classes.listItemText, {
                  [classes.listItemText]: open,
                  [classes.listItemTextHide]: !open,
                })}
                classes={{ primary: classes.listItemText }}
                primary={intl.formatMessage({ id: 'app.barmenu.roles' })}
              />
            </ListItem>
          )}
          {isAllowed(user.permissions, ['tariff_read']) && (
            <ListItem
              className={clsx(classes.listItem, {
                [classes.activeListItem]: pathname === '/controlRate',
                [classes.listItem]: pathname !== '/controlRate',
              })}
              component={NavLink}
              onClick={close}
              to="/controlRate"
            >
              <Tooltip
                placement="right"
                title={intl.formatMessage({ id: 'app.barmenu.feeCtrl' })}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <Fee />
                </ListItemIcon>
              </Tooltip>
              <ListItemText
                className={clsx(classes.listItemText, {
                  [classes.listItemText]: open,
                  [classes.listItemTextHide]: !open,
                })}
                classes={{ primary: classes.listItemText }}
                primary={intl.formatMessage({ id: 'app.barmenu.feeCtrl' })}
              />
            </ListItem>
          )}
          {isAllowed(user.permissions, ['metimur_alert_read']) && (
            <ListItem
              className={clsx(classes.listItem, {
                [classes.activeListItem]: pathname === '/notificationDashboard',
                [classes.listItem]: pathname !== '/notificationDashboard',
              })}
              component={NavLink}
              onClick={close}
              to="/notificationDashboard"
            >
              <Tooltip
                placement="right"
                title={intl.formatMessage({ id: 'app.barmenu.alerts' })}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <ReportProblem />
                </ListItemIcon>
              </Tooltip>
              <ListItemText
                className={clsx(classes.listItemText, {
                  [classes.listItemText]: open,
                  [classes.listItemTextHide]: !open,
                })}
                classes={{ primary: classes.listItemText }}
                primary={intl.formatMessage({ id: 'app.barmenu.alerts' })}
              />
            </ListItem>
          )}

          {isAllowed(user.permissions, ['invoice_read']) && (
            <ListItem
              className={clsx(classes.listItem, {
                [classes.activeListItem]: pathname === '/reports',
                [classes.listItem]: pathname !== '/reports',
              })}
              component={NavLink}
              onClick={close}
              to="/reports"
            >
              <Tooltip
                placement="right"
                title={intl.formatMessage({ id: 'app.barmenu.reports' })}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <Description />
                </ListItemIcon>
              </Tooltip>
              <ListItemText
                className={clsx(classes.listItemText, {
                  [classes.listItemText]: open,
                  [classes.listItemTextHide]: !open,
                })}
                classes={{ primary: classes.listItemText }}
                primary={intl.formatMessage({ id: 'app.barmenu.reports' })}
              />
            </ListItem>
          )}

          {isAllowed(user.permissions, ['invoice_read']) && (
            <ListItem
              className={clsx(classes.listItem, {
                [classes.activeListItem]: pathname === '/invoices',
                [classes.listItem]: pathname !== '/invoices',
              })}
              component={NavLink}
              onClick={close}
              to="/invoices"
            >
              <Tooltip placement="right" title="Recibos">
                <ListItemIcon className={classes.listItemIcon}>
                  <LocalAtm />
                </ListItemIcon>
              </Tooltip>
              <ListItemText
                className={clsx(classes.listItemText, {
                  [classes.listItemText]: open,
                  [classes.listItemTextHide]: !open,
                })}
                classes={{ primary: classes.listItemText }}
                primary="Recibos"
              />
            </ListItem>
          )}
          <ListItem
            className={clsx(classes.listItem, {
              [classes.activeListItem]: pathname === '/help',
              [classes.listItem]: pathname !== '/help',
            })}
            component={NavLink}
            onClick={close}
            to="/help"
          >
            <Tooltip
              placement="right"
              title={intl.formatMessage({ id: 'app.barmenu.help' })}
            >
              <ListItemIcon className={classes.listItemIcon}>
                <Info />
              </ListItemIcon>
            </Tooltip>
            <ListItemText
              className={clsx(classes.listItemText, {
                [classes.listItemText]: open,
                [classes.listItemTextHide]: !open,
              })}
              classes={{ primary: classes.listItemText }}
              primary={intl.formatMessage({ id: 'app.barmenu.help' })}
            />
          </ListItem>
        </List>
        <Divider className={classes.listDivider} />
        <List className={classes.footerMenu} component="div">
          <ListItem
            className={classes.listItem}
            component="a"
            onClick={() => finishSession()}
          >
            <Tooltip
              placement="right"
              title={intl.formatMessage({ id: 'app.barmenu.logout' })}
            >
              <ListItemIcon className={classes.listItemIcon}>
                <ExitToApp />
              </ListItemIcon>
            </Tooltip>
            <ListItemText
              className={clsx(classes.listItemText, {
                [classes.listItemText]: open,
                [classes.listItemTextHide]: !open,
              })}
              classes={{ primary: classes.listItemText }}
              primary={intl.formatMessage({ id: 'app.barmenu.logout' })}
            />
          </ListItem>
        </List>
      </nav>
    </Drawer>
  );
}
